import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';

import { describeRxPrefillIssue, type RxPrefillIssue } from '../model/prefillLookups';

interface RxPrefillIssueAlertProps {
  issue: RxPrefillIssue;
  isRetrying: boolean;
  onRetry: () => Promise<void>;
  onReject: (reason: string) => Promise<void>;
}

export function RxPrefillIssueAlert({
  issue,
  isRetrying,
  onRetry,
  onReject,
}: RxPrefillIssueAlertProps) {
  const [isConfirmingReject, setIsConfirmingReject] = useState(false);
  // Null until the clinician edits it, so the default follows the issue as it changes.
  const [editedReason, setEditedReason] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const rejectReason = editedReason ?? describeRxPrefillIssue(issue);

  const cancelReject = () => {
    setIsConfirmingReject(false);
    setEditedReason(null);
  };

  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      return;
    }
    setIsRejecting(true);
    try {
      await onReject(reason);
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <Alert variant="destructive">
      <AlertTitle>This prescription couldn&rsquo;t be prepared</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {issue.medicationFailed ? (
            <li>The medication details could not be loaded from DoseSpot.</li>
          ) : null}
          {issue.pharmacy === 'lookup-failed' ? (
            <li>
              The patient&rsquo;s pharmacies could not be loaded. Retry, or choose a pharmacy.
            </li>
          ) : null}
          {issue.pharmacy === 'not-found' ? (
            <li>The patient has no saved pharmacy. Choose one below.</li>
          ) : null}
        </ul>

        {!isConfirmingReject ? (
          <div className="flex gap-2 pt-3">
            <Button
              type="button"
              variant="secondary"
              radius="pill"
              onClick={onRetry}
              disabled={isRetrying}
              aria-busy={isRetrying}
            >
              {isRetrying ? 'Retrying…' : 'Retry'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              radius="pill"
              onClick={() => setIsConfirmingReject(true)}
              disabled={isRetrying}
            >
              Reject
            </Button>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2 pt-3">
            <Textarea
              aria-label="Reason for rejecting"
              value={rejectReason}
              onChange={(event) => setEditedReason(event.target.value)}
              rows={3}
              disabled={isRejecting}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                radius="pill"
                onClick={cancelReject}
                disabled={isRejecting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="destructive"
                radius="pill"
                onClick={submitReject}
                disabled={isRejecting || !rejectReason.trim()}
                aria-busy={isRejecting}
              >
                {isRejecting ? 'Rejecting…' : 'Reject prescription'}
              </Button>
            </div>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
